import {
  ReactElement,
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react';
import { ColumnDescription } from 'react-bootstrap-table-next';
import { find } from 'lodash';

import { useSubscriptionFeatures, useLocalStorage } from '~hooks/index';
import { isEqualArrays } from '~utils/commons';
import { AccessFeatureCommonProps } from '~components/AccessFeature/types';

import { ColumnToggleProviderProps, ColumnsToggleModel } from './types';
import ColumnsHideableToggle from './ColumnsHideableToggle';

interface ColumnToggleContextValue {
  toggleModel: ColumnsToggleModel | undefined;
  setToggleModel: (newModel: ColumnsToggleModel) => void;
  columns: ColumnDescription[];
  excludeHideableFieldColumns: string[];
}

const ColumnToggleContext = createContext<ColumnToggleContextValue>({
  toggleModel: undefined,
  setToggleModel: () => {},
  columns: [],
  excludeHideableFieldColumns: [],
});

export const useColumnsToggleContext = (): ColumnToggleContextValue =>
  useContext(ColumnToggleContext);

const initializeColumnToggleList = (
  columns: ColumnDescription[],
): ColumnDescription[] => {
  const columnAction = find(columns, { dataField: 'actions' });

  if (columnAction) {
    columnAction.headerFormatter = () => <ColumnsHideableToggle />;
  } else {
    columns.push({
      dataField: 'actions',
      isDummyField: true,
      text: '',
      classes: 'truncated-two-lines',
      headerFormatter: () => <ColumnsHideableToggle />,
      headerStyle: { width: 60 },
    });
  }

  return columns;
};

const ColumnToggleProvider = ({
  keyField,
  columns: columnsProps = [],
  data = [],
  bootstrap4 = true,
  toggleHideableColumns,
  excludeHideableFieldColumns = [],
  hideableStorageKey,
  children,
}: ColumnToggleProviderProps): ReactElement => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const getColumnHiddenValue = useCallback(
    (
      defaultValue?: boolean,
      validSubscriptionFeature?: AccessFeatureCommonProps,
    ) => {
      let hidden = defaultValue ?? false;

      if (
        validSubscriptionFeature &&
        !hasAccessToFeature(
          validSubscriptionFeature.validFeature,
          validSubscriptionFeature.availableToDate,
        )
      ) {
        hidden = true;
      }

      return hidden;
    },
    [hasAccessToFeature],
  );

  const initializeToggleModel = useCallback(
    (columns: ColumnDescription[]): ColumnsToggleModel =>
      columns.reduce((acc, column) => {
        if (column.dataField !== 'actions') {
          acc[column.dataField] = getColumnHiddenValue(
            column.hidden,
            column.validSubscriptionFeature,
          );
        }

        return acc;
      }, {} as ColumnsToggleModel),
    [getColumnHiddenValue],
  );

  const { state: toggleModel, set: setToggleModel } =
    useLocalStorage<ColumnsToggleModel>({
      key: hideableStorageKey,
      initialValue: initializeToggleModel(columnsProps),
    });

  const hasNewColumns = useCallback(() => {
    if (toggleModel) {
      const columnsKeys = columnsProps
        .filter((column) => column.dataField !== 'actions')
        .map((column) => column.dataField);

      const toggleModelKeys = Object.keys(toggleModel);

      return !isEqualArrays(columnsKeys, toggleModelKeys);
    }

    return true;
  }, [columnsProps, toggleModel]);

  useLayoutEffect(() => {
    if (hasNewColumns()) {
      setToggleModel({
        ...initializeToggleModel(columnsProps),
        ...toggleModel,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(() => {
    if (toggleHideableColumns) {
      const transformColumns = initializeColumnToggleList(columnsProps).map(
        (column) => ({
          ...column,
          hidden: getColumnHiddenValue(
            toggleModel?.[column.dataField] ?? column.hidden,
            column.validSubscriptionFeature,
          ),
        }),
      );

      return transformColumns;
    }

    return columnsProps;
  }, [columnsProps, toggleHideableColumns, getColumnHiddenValue, toggleModel]);

  return (
    <ColumnToggleContext.Provider
      value={{
        toggleModel,
        setToggleModel,
        columns,
        excludeHideableFieldColumns,
      }}
    >
      {children({ keyField, columns, data, bootstrap4 })}
    </ColumnToggleContext.Provider>
  );
};

export default ColumnToggleProvider;
